"use client";

import { useState } from "react";
import {
  ErrorNotice,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { CARD, WIDGET_H2 } from "@/components/tool-kit/styles";
import { MACRO_CHART_STRINGS } from "@/components/tools/econ-macro/line-figure";
import { FiscalResults } from "@/components/tools/econ-macro/multiplier-fiscal";
import { MoneyResults } from "@/components/tools/econ-macro/multiplier-money";
import { useToolUi } from "@/components/tools/tool-ui";
import { parseNumberInput, parseNumberOr } from "@/lib/parse-number";
import {
  type FiscalMultipliers,
  fiscalMultipliers,
  type MoneyResult,
  moneyMultiplier,
} from "@/lib/tool-kit/econ-macro/multipliers";

/** English strings. A translated content file overrides these through `ui`. */
export const MULTIPLIER_CALCULATOR_STRINGS = {
  ...MACRO_CHART_STRINGS,
  modeLabel: "Which multiplier?",
  modeFiscal: "Spending, tax and balanced-budget multipliers",
  modeMoney: "Money (deposit) multiplier",
  inputsHeading: "Your numbers",
  knownLabel: "You know the",
  knownMpc: "Marginal propensity to consume (MPC)",
  knownMps: "Marginal propensity to save (MPS)",
  mpcField: "MPC — as a decimal, such as 0.8",
  mpsField: "MPS — as a decimal, such as 0.2",
  mpcHint:
    "The share of each extra dollar of income that is spent. It must be at least 0 and below 1.",
  mpsHint:
    "The share of each extra dollar of income that is saved. MPC + MPS = 1, so MPS must be above 0 and at most 1.",
  spendingChange: "Change in government spending (ΔG)",
  taxChange: "Change in lump-sum taxes (ΔT)",
  policyHint:
    "Use any one unit for both — dollars, millions, billions. Enter a tax cut or a spending cut as a negative number. Leave a field at 0 to ignore it.",
  reserveRatio: "Required reserve ratio (%)",
  excessRatio: "Excess reserves ÷ deposits (%)",
  cashRatio: "Currency held by the public ÷ deposits (%)",
  newBase: "New reserves injected into the banking system",
  moneyHint:
    "Leave excess reserves and currency at 0 for the basic textbook model, where the multiplier is 1 ÷ the reserve ratio.",
  resultsHeading: "Results",
  invalidMpc: "Enter an MPC from 0 up to (but not including) 1.",
  invalidMps: "Enter an MPS above 0 and no more than 1.",
  unbounded:
    "With an MPC of 1 every extra dollar is spent, so each round passes on the whole amount and the multiplier has no finite value. Enter an MPC below 1.",
  invalidAmounts: "Enter ΔG and ΔT as numbers.",
  invalidRatios:
    "Enter ratios of 0% or more, with the reserve ratio plus excess reserves no more than 100%, and new reserves as a number.",
  noLeakage:
    "With no required reserves, no excess reserves and no cash held, nothing limits deposit creation, so there is no finite multiplier. Enter a reserve ratio above 0.",
  spendingMultiplier: "Spending multiplier",
  taxMultiplier: "Tax multiplier",
  transferMultiplier: "Transfer multiplier",
  balancedMultiplier: "Balanced-budget multiplier",
  incomeFromSpending: "Change in income from ΔG",
  incomeFromTax: "Change in income from ΔT",
  incomeTotal: "Total change in income (ΔY)",
  workingHeading: "Working",
  workSpending:
    "Spending multiplier = 1 ÷ (1 − MPC) = 1 ÷ (1 − {mpc}) = 1 ÷ {mps} = {k}",
  workTax: "Tax multiplier = −MPC ÷ (1 − MPC) = −{mpc} ÷ {mps} = {kt}",
  workTransfer: "Transfer multiplier = MPC ÷ (1 − MPC) = {mpc} ÷ {mps} = {ktr}",
  workBalanced: "Balanced budget: {k} + ({kt}) = {kb}",
  workIncome: "ΔY = {k} × {dg} + ({kt}) × {dt} = {dy}",
  workFirstRound:
    "First-round change in spending = ΔG − MPC × ΔT = {dg} − {mpc} × {dt} = {first}",
  roundsHeading: "Round by round",
  roundsIntro:
    "Each round, households spend the MPC share of the extra income they just received. After {rounds} rounds the chain has delivered {share}% of its final effect.",
  roundColumn: "Round",
  newSpendingColumn: "New spending",
  cumulativeColumn: "Cumulative change in income",
  shareColumn: "Share of final effect",
  seriesCumulative: "Cumulative change in income",
  seriesLimit: "Final change in income (ΔY)",
  chartTitleFiscal: "How the multiplier builds up round by round",
  chartXFiscal: "Round of spending",
  chartYFiscal: "Change in income",
  chartDescriptionFiscal:
    "A line graph of the cumulative change in income after each of {rounds} rounds of spending, rising toward the final change of {total}.",
  assumptionsFiscal:
    "Simple-model assumptions: a closed economy (no imports), lump-sum taxes (no income-tax rate), fixed prices, spare capacity, and no crowding out of private spending or investment. Real multipliers are smaller.",
  depositMultiplier: "Deposit multiplier",
  moneyMultiplierCard: "Money multiplier (M ÷ base)",
  simpleMultiplier: "Simple multiplier (1 ÷ rr)",
  depositsCreated: "Maximum total deposits",
  moneySupplyChange: "Change in the money supply",
  newLending: "New lending supported",
  workDeposit:
    "Deposit multiplier = 1 ÷ (rr + e + c) = 1 ÷ ({rr} + {e} + {c}) = 1 ÷ {leak} = {dm}",
  workMoney:
    "Money multiplier = (1 + c) ÷ (rr + e + c) = (1 + {c}) ÷ {leak} = {mm}",
  workDeposits:
    "Total deposits = new reserves × deposit multiplier = {base} × {dm} = {deposits}",
  workLending:
    "New lending = total deposits − new reserves = {deposits} − {base} = {lending}",
  depositRoundsHeading: "Deposit expansion, round by round",
  depositRoundsIntro:
    "Each bank keeps its required and excess reserves and lends the rest; the loan is spent and redeposited at the next bank. After {rounds} rounds {share}% of the final deposits exist.",
  newDepositColumn: "New deposit",
  reservesColumn: "Reserves held",
  loansColumn: "New loans",
  totalDepositsColumn: "Total deposits",
  seriesDeposits: "Total deposits",
  seriesDepositLimit: "Maximum total deposits",
  chartTitleMoney: "Deposit expansion round by round",
  chartXMoney: "Round of lending",
  chartYMoney: "Total deposits",
  chartDescriptionMoney:
    "A line graph of total deposits after each of {rounds} rounds of lending, rising toward the maximum of {total}.",
  cashNote:
    "The round-by-round table is shown for the basic model only. With a currency drain, each round also leaks cash out of the banking system, so the chain is not a single geometric series.",
  assumptionsMoney:
    "Textbook assumptions: banks lend every dollar above their reserves, borrowers redeposit everything they borrow (apart from the cash ratio you entered), and the central bank sets the required ratio. Real banks lend on demand, not on reserves, so treat this as a classroom model.",
} as const;

export type Strings = typeof MULTIPLIER_CALCULATOR_STRINGS;
type Mode = "fiscal" | "money";
type Known = "mpc" | "mps";

function FiscalForm({
  strings,
  known,
  setKnown,
  propensity,
  setPropensity,
  spending,
  setSpending,
  tax,
  setTax,
}: {
  strings: Strings;
  known: Known;
  setKnown: (value: Known) => void;
  propensity: string;
  setPropensity: (value: string) => void;
  spending: string;
  setSpending: (value: string) => void;
  tax: string;
  setTax: (value: string) => void;
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <SelectField
        id="mc-known"
        label={strings.knownLabel}
        onChange={setKnown}
        options={[
          { value: "mpc", label: strings.knownMpc },
          { value: "mps", label: strings.knownMps },
        ]}
        value={known}
      />
      <TextField
        hint={known === "mpc" ? strings.mpcHint : strings.mpsHint}
        id="mc-propensity"
        label={known === "mpc" ? strings.mpcField : strings.mpsField}
        numeric
        onChange={setPropensity}
        value={propensity}
      />
      <TextField
        hint={strings.policyHint}
        id="mc-dg"
        label={strings.spendingChange}
        numeric
        onChange={setSpending}
        value={spending}
      />
      <TextField
        id="mc-dt"
        label={strings.taxChange}
        numeric
        onChange={setTax}
        value={tax}
      />
    </div>
  );
}

interface FiscalOutcome {
  problem: string | null;
  multipliers: FiscalMultipliers | null;
}

function resolveFiscal(
  known: Known,
  value: number | null,
  strings: Strings
): FiscalOutcome {
  const invalid = known === "mpc" ? strings.invalidMpc : strings.invalidMps;
  if (value === null || (known === "mps" && (value <= 0 || value > 1))) {
    return { problem: invalid, multipliers: null };
  }
  const result = fiscalMultipliers(known === "mpc" ? value : 1 - value);
  if (result === "out-of-range") {
    return { problem: strings.invalidMpc, multipliers: null };
  }
  if (result === "unbounded") {
    return { problem: strings.unbounded, multipliers: null };
  }
  return { problem: null, multipliers: result };
}

function Fiscal({ strings }: { strings: Strings }) {
  const [known, setKnown] = useState<Known>("mpc");
  const [propensity, setPropensity] = useState("0.8");
  const [spending, setSpending] = useState("100");
  const [tax, setTax] = useState("0");
  const dg = parseNumberInput(spending);
  const dt = parseNumberInput(tax);
  const outcome = resolveFiscal(known, parseNumberInput(propensity), strings);
  const { multipliers } = outcome;
  const problem =
    outcome.problem ??
    (dg === null || dt === null ? strings.invalidAmounts : null);

  return (
    <>
      <section className={`${CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.inputsHeading}</h2>
        <FiscalForm
          known={known}
          propensity={propensity}
          setKnown={setKnown}
          setPropensity={setPropensity}
          setSpending={setSpending}
          setTax={setTax}
          spending={spending}
          strings={strings}
          tax={tax}
        />
      </section>
      <section aria-live="polite" className="space-y-5">
        <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
        {problem ? <ErrorNotice>{problem}</ErrorNotice> : null}
        {multipliers && !problem ? (
          <FiscalResults
            dg={dg ?? 0}
            dt={dt ?? 0}
            multipliers={multipliers}
            strings={strings}
          />
        ) : null}
      </section>
    </>
  );
}

interface MoneyOutcome {
  problem: string | null;
  result: MoneyResult | null;
}

function resolveMoney(
  input: { rr: number | null; e: number; c: number; newBase: number | null },
  strings: Strings
): MoneyOutcome {
  const { rr, e, c, newBase } = input;
  if (rr === null || newBase === null) {
    return { problem: strings.invalidRatios, result: null };
  }
  const outcome = moneyMultiplier({
    reserveRatio: rr / 100,
    excessRatio: e / 100,
    cashRatio: c / 100,
    newBase,
  });
  if (outcome === "invalid-ratio") {
    return { problem: strings.invalidRatios, result: null };
  }
  if (outcome === "no-leakage") {
    return { problem: strings.noLeakage, result: null };
  }
  return { problem: null, result: outcome };
}

function Money({ strings }: { strings: Strings }) {
  const [reserve, setReserve] = useState("10");
  const [excess, setExcess] = useState("0");
  const [cash, setCash] = useState("0");
  const [base, setBase] = useState("1000");
  const rr = parseNumberInput(reserve);
  const e = parseNumberOr(excess, 0);
  const c = parseNumberOr(cash, 0);
  const newBase = parseNumberInput(base);
  const { problem, result } = resolveMoney({ rr, e, c, newBase }, strings);

  return (
    <>
      <section className={`${CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.inputsHeading}</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="mc-rr"
            label={strings.reserveRatio}
            numeric
            onChange={setReserve}
            value={reserve}
          />
          <TextField
            id="mc-base"
            label={strings.newBase}
            numeric
            onChange={setBase}
            value={base}
          />
          <TextField
            hint={strings.moneyHint}
            id="mc-excess"
            label={strings.excessRatio}
            numeric
            onChange={setExcess}
            value={excess}
          />
          <TextField
            id="mc-cash"
            label={strings.cashRatio}
            numeric
            onChange={setCash}
            value={cash}
          />
        </div>
      </section>
      <section aria-live="polite" className="space-y-5">
        <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
        {problem ? <ErrorNotice>{problem}</ErrorNotice> : null}
        {result && !problem && rr !== null && newBase !== null ? (
          <MoneyResults
            cash={c / 100}
            excess={e / 100}
            newBase={newBase}
            reserve={rr / 100}
            result={result}
            strings={strings}
          />
        ) : null}
      </section>
    </>
  );
}

export default function MultiplierCalculatorWidget() {
  const strings = useToolUi(MULTIPLIER_CALCULATOR_STRINGS);
  const [mode, setMode] = useState<Mode>("fiscal");
  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.modeLabel}
        name="mc-mode"
        onChange={setMode}
        options={[
          { value: "fiscal", label: strings.modeFiscal },
          { value: "money", label: strings.modeMoney },
        ]}
        value={mode}
      />
      {mode === "fiscal" ? (
        <Fiscal strings={strings} />
      ) : (
        <Money strings={strings} />
      )}
    </div>
  );
}
